import React, { useState, useEffect } from "react";
import { useAuth } from "../../context/AuthProvider";
import API_BASE_URL from "../../config/apiConfig";
import TherapistSidebar from "../../components/TherapistSidebar";
import StatusDropdown from "../../components/StatusDropdown";
import edit from "../../assets/images/edit-pen.svg";

interface Appointment {
  id: number;
  therapist_id: string;
  patient_name: string;
  type: string; // "therapy" sau "activity"
  appointment_description: string;
  status: string;
  start_time: string; // Ex: "2025-01-29T09:00:00"
  end_time: string; // Ex: "2025-01-29T09:30:00"
}

interface AppointmentStatus {
  key: string;
  value: string;
  type: string;
  color: string;
  activity_key?: string;
}

interface TimeInterval {
  start: string; // "09:00"
  end: string; // "09:30"
}

interface AppointmentType {
  key: string;
  value: string;
}

interface Activity {
  key: string;
  value: string;
}

interface TherapyType {
  key: string;
  value: string;
}

function formatDateToYMD(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

const TherapistAppointments: React.FC = () => {
  const { token, userId } = useAuth();
  const [timeIntervals, setTimeIntervals] = useState<TimeInterval[]>([]);
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [appointmentStatuses, setAppointmentStatuses] = useState<AppointmentStatus[]>([]);
  const [appointmentTypes, setAppointmentTypes] = useState<AppointmentType[]>([]);
  const [activities, setActivities] = useState<Activity[]>([]);
  const [therapyTypes, setTherapyTypes] = useState<TherapyType[]>([]);
  const [selectedDate, setSelectedDate] = useState(formatDateToYMD(new Date()));

  const [isCollapsed, setIsCollapsed] = useState<boolean>(() => {
    return localStorage.getItem("sidebarCollapsedTherapist") === "true";
  });

  // ===== EDIT MODE: doar pentru terapii, doar pe o programare la un moment dat =====
  const [editingTherapyAppointmentId, setEditingTherapyAppointmentId] = useState<number | null>(null);
  const [editingTherapyValue, setEditingTherapyValue] = useState<string>("");
  const [savingTherapy, setSavingTherapy] = useState<boolean>(false);

  const startEditTherapy = (appointmentId: number, currentTherapyKey: string) => {
    setEditingTherapyAppointmentId(appointmentId);
    setEditingTherapyValue(currentTherapyKey);
  };

  const cancelEditTherapy = () => {
    setEditingTherapyAppointmentId(null);
    setEditingTherapyValue("");
    setSavingTherapy(false);
  };

  // ================== FETCH TIME INTERVALS ==================
  useEffect(() => {
    const fetchTimeIntervals = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/types/time-intervals?date=${selectedDate}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!response.ok) throw new Error("Nu s-au putut prelua intervalele orare.");
        const data = await response.json();
        setTimeIntervals(data.data || []);
      } catch (error) {
        console.error("Eroare la încărcarea intervalelor orare:", error);
      }
    };

    if (token && selectedDate) fetchTimeIntervals();
  }, [token, selectedDate]);

  // ================== FETCH DROPDOWNS ==================
  useEffect(() => {
    const fetchDropdownData = async () => {
      try {
        const [activitiesResponse, therapyResponse, therapyTypeResponse] = await Promise.all([
          fetch(`${API_BASE_URL}/types/activities`, { headers: { Authorization: `Bearer ${token}` } }),
          fetch(`${API_BASE_URL}/types/appointment-type`, { headers: { Authorization: `Bearer ${token}` } }),
          fetch(`${API_BASE_URL}/types/therapy-type`, { headers: { Authorization: `Bearer ${token}` } }),
        ]);

        const therapyData = await therapyResponse.json();
        const activitiesData = await activitiesResponse.json();
        const therapyTypeData = await therapyTypeResponse.json();

        setAppointmentTypes(therapyData.data || []);
        setActivities(activitiesData.data || []);
        setTherapyTypes(therapyTypeData.data || []);
      } catch (error) {
        console.error("Eroare la încărcarea datelor:", error);
      }
    };

    if (token) fetchDropdownData();
  }, [token]);

  // ================== FETCH APPOINTMENTS (by userId + date) ==================
  useEffect(() => {
    if (!token || !userId || !selectedDate) return;

    const controller = new AbortController();

    const fetchAppointments = async () => {
      try {
        const res = await fetch(
          `${API_BASE_URL}/appointments/get-appointments/${userId}?date=${selectedDate}`,
          {
            headers: { Authorization: `Bearer ${token}` },
            signal: controller.signal,
          }
        );

        if (!res.ok) throw new Error("Nu s-au putut prelua programările.");

        const data = await res.json();
        setAppointments(data.data || []);
      } catch (err) {
        if (err instanceof DOMException && err.name === "AbortError") return;
        console.error("Eroare la încărcarea programărilor:", err);
      }
    };

    fetchAppointments();

    // dacă schimbăm data, ieșim din edit mode (ca să nu “rămână blocat”)
    cancelEditTherapy();

    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, userId, selectedDate]);

  // ================== FETCH APPOINTMENT STATUSES ==================
  useEffect(() => {
    const fetchStatuses = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/types/appointment-status`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!response.ok) throw new Error("Nu s-au putut prelua statusurile.");
        const data = await response.json();
        setAppointmentStatuses(data.data || []);
      } catch (error) {
        console.error("Eroare la încărcarea statusurilor:", error);
      }
    };

    if (token) fetchStatuses();
  }, [token]);

  // ================== UPDATE STATUS ==================
  const handleStatusChange = async (appointmentId: number, newStatus: string) => {
    try {
      const response = await fetch(`${API_BASE_URL}/appointments/update-status/${appointmentId}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ status: newStatus }),
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data?.detail || "Eroare la actualizarea statusului.");
      }

      setAppointments((prev) =>
        prev.map((app) => (app.id === appointmentId ? { ...app, status: newStatus } : app))
      );
    } catch (error) {
      console.error(error);
      alert("Eroare la actualizarea statusului.");
    }
  };

  // ================== SAVE THERAPY (ONLY FOR type=therapy) ==================
  const saveTherapyChange = async (appointmentId: number) => {
    try {
      setSavingTherapy(true);

      const response = await fetch(`${API_BASE_URL}/appointments/update-therapy/${appointmentId}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ therapy_key: editingTherapyValue }),
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data?.detail || "Eroare la actualizarea terapiei.");
      }

      // update local
      setAppointments((prev) =>
        prev.map((app) =>
          app.id === appointmentId ? { ...app, appointment_description: editingTherapyValue } : app
        )
      );

      cancelEditTherapy();
    } catch (error) {
      console.error(error);
      alert("Eroare la schimbarea terapiei.");
      setSavingTherapy(false);
    }
  };

  // ================== HELPERS ==================
  const getAppointmentTypeValue = (key: string) => {
    const appointmentType = appointmentTypes.find((item) => item.key === key);
    return appointmentType ? appointmentType.value : key;
  };

  const getActivitiesValue = (key: string) => {
    const activity = activities.find((item) => item.key === key);
    return activity ? activity.value : key;
  };

  const getTherapyTypeValue = (key: string) => {
    const therapyType = therapyTypes.find((item) => item.key === key);
    return therapyType ? therapyType.value : key;
  };

  return (
    <div className={`dashboard-container ${isCollapsed ? "collapsed" : ""}`}>
      <TherapistSidebar onCollapseChange={(collapsed) => setIsCollapsed(collapsed)} />

      <div className="page-content">
        <h1 className="page-title">Programările mele</h1>

        <div className="table-top">
          <div className="page-subtitle">Programări</div>
          <div className="appointments-actions"></div>

          <div className="appointment-date">
            <label>Data: </label>
            <input
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
            />
          </div>
        </div>

        <div className="table-rounded-container therapist-appointments-table">
          <table className="table-rounded therapist-appointments" style={{ borderCollapse: "collapse", width: "100%" }}>
            <thead>
              <tr>
                <th>Interval orar</th>
                <th>Tipul programării</th>
                <th>Detalii programare</th>
                <th className="appointment-status-col">Status</th>
              </tr>
            </thead>

            <tbody>
              {timeIntervals.map((interval, rowIndex) => {
                const intervalStartFull = `${selectedDate}T${interval.start}:00`;
                const matchedAppointments = appointments.filter(
                  (app) => app.start_time === intervalStartFull
                );

                const cellClass = rowIndex % 2 === 0 ? "even-cell" : "odd-cell";

                if (matchedAppointments.length === 0) {
                  return (
                    <tr key={interval.start}>
                      <td className={cellClass}>{`${interval.start} - ${interval.end}`}</td>
                      <td className={cellClass}>-</td>
                      <td className={cellClass}>-</td>
                      <td className={cellClass}>-</td>
                    </tr>
                  );
                }

                return matchedAppointments.map((appointment, index) => {
                  const filteredStatuses = (() => {
                    if (appointment.type === "therapy") {
                      return appointmentStatuses.filter((st) => st.type === "therapy");
                    }
                    // Activitate: întâi statusurile specifice acestei activități
                    const specific = appointmentStatuses.filter(
                      (st) =>
                        st.type === "activity" &&
                        st.activity_key === appointment.appointment_description
                    );
                    if (specific.length > 0) return specific;
                    // Fallback: statusurile generice de activitate
                    return appointmentStatuses.filter(
                      (st) => st.type === "activity" && !st.activity_key
                    );
                  })();

                  const isTherapy = appointment.type === "therapy";
                  const isEditingThisRow = editingTherapyAppointmentId === appointment.id;

                  return (
                    <tr key={`${interval.start}-${appointment.id}`}>
                      {index === 0 && (
                        <td className={cellClass} rowSpan={matchedAppointments.length}>
                          {`${interval.start} - ${interval.end}`}
                        </td>
                      )}

                      <td className={cellClass + " appointment-type"}>
                        {getAppointmentTypeValue(appointment.type)}
                      </td>

                      <td className={cellClass}>
                        {isTherapy ? (
                          <>
                            <div className="appointment-value appointment-patient">
                              <span className="appointment-text">Pacient:</span> {appointment.patient_name}
                            </div>

                            <div className="appointment-value appointment-type">
                              <span className="appointment-text">Terapie:</span>{" "}

                              {/* AICI modificăm DOAR celula, restul rămâne identic */}
                              {!isEditingThisRow ? (
                                <>
                                  {getTherapyTypeValue(appointment.appointment_description)}

                                  <img
                                    src={edit}
                                    alt="edit"
                                    title="Editează terapia"
                                    style={{ width: 14, height: 14, marginLeft: 0, cursor: "pointer", verticalAlign: "middle" }}
                                    onClick={() => startEditTherapy(appointment.id, appointment.appointment_description)}
                                  />
                                </>
                              ) : (
                                <>
                                  <select
                                    value={editingTherapyValue}
                                    onChange={(e) => setEditingTherapyValue(e.target.value)}
                                    style={{ marginLeft: 6 }}
                                  >
                                    {therapyTypes.map((t) => (
                                      <option key={t.key} value={t.key}>
                                        {t.value}
                                      </option>
                                    ))}
                                  </select>

                                  <button
                                    className="primary-btn"
                                    style={{ marginLeft: 5 }}
                                    disabled={savingTherapy}
                                    onClick={() => saveTherapyChange(appointment.id)}
                                  >
                                    Salvează
                                  </button>

                                  <button
                                    className="cancel-btn-9p"
                                    style={{ marginLeft: 0 }}
                                    disabled={savingTherapy}
                                    onClick={cancelEditTherapy}
                                  >
                                    Anulează
                                  </button>
                                </>
                              )}
                            </div>
                          </>
                        ) : (
                          <>
                            <div className="appointment-value appointment-type">
                              <span className="appointment-text">Activitate:</span> {getActivitiesValue(appointment.appointment_description)}
                            </div>

                            {appointment.appointment_description === "evaluare" && (
                              <div className="appointment-value appointment-patient">
                                <span className="appointment-text">Pacient:</span> {appointment.patient_name}
                              </div>
                            )}
                          </>
                        )}
                      </td>

                      <td className={cellClass + " appointment-status"}>
                        <StatusDropdown
                          statuses={filteredStatuses}
                          currentStatus={appointment.status}
                          onChange={(newStatus) => handleStatusChange(appointment.id, newStatus)}
                        />
                      </td>
                    </tr>
                  );
                });
              })}
            </tbody>

          </table>
        </div>
      </div>
    </div>
  );
};

export default TherapistAppointments;